import { useEffect, useState, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Hammer, Briefcase, FileText, Route, Plus, Pencil, Trash2, Search, Loader2, Brain, Save, X, Copy, ChevronRight, Upload, Receipt, Mail, Star, ClipboardList, GitBranch, ArrowRight, Tag, ArrowLeft, Package, CheckCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { InvoiceEditor } from "../components/InvoiceEditor";
import { EmailEditor } from "../components/EmailEditor";
import { DocumentEditor } from "../components/DocumentEditor";

const inputCls = "bg-[#0b0811] border-white/10 text-white focus:border-[#f7c935]";

export default function BuilderPage() {
  const { axiosAuth, user } = useAuth();
  const { t } = useTranslation();
  const api = axiosAuth();
  const [searchParams] = useSearchParams();
  const isTenantCtx = user?.role === "tenant_admin" || user?.role === "tenant_member";
  const initialTab = searchParams.get("tab") || (isTenantCtx ? "questionnaires" : "services");
  const [tab, setTab] = useState(initialTab);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  // Data
  const [catalogServices, setCatalogServices] = useState([]);
  const [accTemplates, setAccTemplates] = useState([]);
  const [docTemplates, setDocTemplates] = useState([]);
  const [invoiceTemplates, setInvoiceTemplates] = useState([]);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [questionnaires, setQuestionnaires] = useState([]);

  // Dialogs
  const [showDialog, setShowDialog] = useState(false);
  const [dialogType, setDialogType] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  // AI generation (doc templates)
  const [showAI, setShowAI] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [generatingAI, setGeneratingAI] = useState(false);

  // AI generation (acc templates)
  const [showAccAI, setShowAccAI] = useState(false);
  const [accAiPrompt, setAccAiPrompt] = useState("");
  const [generatingAccAI, setGeneratingAccAI] = useState(false);

  // AI generation (services)
  const [showSvcAI, setShowSvcAI] = useState(false);
  const [svcAiPrompt, setSvcAiPrompt] = useState("");
  const [svcAiKind, setSvcAiKind] = useState("fiche_info");
  const [generatingSvcAI, setGeneratingSvcAI] = useState(false);

  // AI generation (questionnaires)
  const [showQstAI, setShowQstAI] = useState(false);
  const [qstAiPrompt, setQstAiPrompt] = useState("");
  const [generatingQstAI, setGeneratingQstAI] = useState(false);

  // File upload for questionnaires
  const [uploadingQstFile, setUploadingQstFile] = useState(false);
  const qstFileInputRef = useRef(null);

  // File upload for services
  const [uploadingSvcFile, setUploadingSvcFile] = useState(false);
  const svcFileInputRef = useRef(null);

  // File upload for AI generation
  const [uploadingFile, setUploadingFile] = useState(false);
  const fileInputRef = useRef(null);

  // File upload for accompagnement templates
  const [uploadingAccFile, setUploadingAccFile] = useState(false);
  const accFileInputRef = useRef(null);

  // Template phases editor
  const [phases, setPhases] = useState([]);

  // Doc template fields editor
  const [fields, setFields] = useState([]);

  // Questionnaire conditions editor
  const [qstConditions, setQstConditions] = useState([]);

  // Tags/Balises
  const [tags, setTags] = useState([]);
  const [tagCategories, setTagCategories] = useState([]);
  const [newTag, setNewTag] = useState({ key: "", label: "", category: "entreprise" });
  const [editingTag, setEditingTag] = useState(null);
  const [savingTag, setSavingTag] = useState(false);

  // Tenant Offers
  const [offers, setOffers] = useState([]);
  const [allAddons, setAllAddons] = useState([]);

  // Fullscreen editor mode (for advanced editors)
  const [editorMode, setEditorMode] = useState(null); // "inv_template" | "email_template" | "doc_template" | null

  const loadAll = async () => {
    try {
      const [sRes, tRes, dRes, invTplRes, emailTplRes, qstRes, tagsRes, offersRes, addonsRes] = await Promise.all([
        api.get("/catalog/services"),
        api.get("/accompaniment-templates"),
        api.get("/catalog/document-templates"),
        api.get("/facturation/templates?doc_type=facture").catch(() => ({ data: [] })),
        api.get("/facturation/templates?doc_type=email").catch(() => ({ data: [] })),
        api.get("/prospect-questionnaires").catch(() => ({ data: [] })),
        api.get("/builder/tags").catch(() => ({ data: { tags: [], categories: [] } })),
        api.get("/tenant-offers/all").catch(() => api.get("/tenant-offers").catch(() => ({ data: [] }))),
        api.get("/addon-modules").catch(() => ({ data: [] })),
      ]);
      setCatalogServices(sRes.data);
      setAccTemplates(tRes.data);
      setDocTemplates(dRes.data);
      setQuestionnaires(qstRes.data || []);
      setTags(tagsRes.data?.tags || []);
      setTagCategories(tagsRes.data?.categories || []);
      setOffers(offersRes.data || []);
      setAllAddons(addonsRes.data || []);
      // Merge devis + avoir into invoice templates too
      const allFact = invTplRes.data || [];
      const devisTpl = await api.get("/facturation/templates?doc_type=devis").catch(() => ({ data: [] }));
      const avoirTpl = await api.get("/facturation/templates?doc_type=avoir").catch(() => ({ data: [] }));
      setInvoiceTemplates([...allFact, ...(devisTpl.data || []), ...(avoirTpl.data || [])]);
      setEmailTemplates(emailTplRes.data || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { loadAll(); }, []);

  const openCreate = (type) => {
    setDialogType(type);
    setEditingId(null);
    if (type === "offer") { openCreateOffer(); return; }
    if (type === "inv_template") {
      setForm({ name: "", type: "facture", is_default: false, primary_color: "#f7c935", header_text: "", footer_text: "", cgv: "", mentions_legales: "", email_subject: "", email_body: "", items: [{ description: "", qty: 1, unit_price: 0, tva: 20 }] });
      setEditorMode("inv_template");
      return;
    } else if (type === "email_template") {
      setForm({ name: "", type: "email", is_default: false, email_subject: "", blocks: [], bg_color: "#ffffff", text_color: "#0b0811" });
      setEditorMode("email_template");
      return;
    } else if (type === "doc_template") {
      setForm({ name: "", description: "", category: "", primary_color: "#461d8a", doc_header: "", doc_body: "", doc_footer: "" });
      setFields([]);
      setEditorMode("doc_template");
      return;
    } else if (type === "questionnaire") {
      setForm({ name: "", description: "", status: "active" });
      setFields([]);
      setQstConditions([]);
    } else {
      setForm({});
    }
    setPhases([]);
    if (type !== "questionnaire") setFields([]);
    setShowDialog(true);
  };

  const openEdit = (type, item) => {
    if (type === "offer") { openEditOffer(item); return; }
    setDialogType(type);
    setEditingId(item.catalog_id || item.template_id || item.questionnaire_id);
    if (type === "service") {
      setForm({ name: item.name, code: item.code, description: item.description, category: item.category, price: item.price, duration: item.duration });
    } else if (type === "acc_template") {
      setForm({ title: item.title, formula: item.formula, description: item.description, duration_months: item.duration_months, price: item.price });
      setPhases(item.phases || []);
    } else if (type === "doc_template") {
      setForm({ name: item.name, description: item.description, category: item.category, primary_color: item.primary_color || "#461d8a", doc_header: item.doc_header || "", doc_body: item.doc_body || "", doc_footer: item.doc_footer || "" });
      setFields(item.fields || []);
      setEditorMode("doc_template");
      return;
    } else if (type === "questionnaire") {
      setForm({ name: item.name, description: item.description, status: item.status || "active" });
      setFields(item.fields || []);
      setQstConditions(item.conditions || []);
    } else if (type === "inv_template") {
      setForm({
        name: item.name || "",
        type: item.type || "facture",
        is_default: item.is_default || false,
        header_text: item.content?.header_text || "",
        footer_text: item.content?.footer_text || "",
        cgv: item.content?.cgv || "",
        mentions_legales: item.content?.mentions_legales || "",
        email_subject: item.content?.email_subject || "",
        email_body: item.content?.email_body || "",
        primary_color: item.content?.primary_color || "#f7c935",
        items: item.content?.items || [{ description: "", qty: 1, unit_price: 0, tva: 20 }],
      });
      setEditorMode("inv_template");
      return;
    } else if (type === "email_template") {
      setForm({
        name: item.name || "",
        type: "email",
        is_default: item.is_default || false,
        email_subject: item.content?.email_subject || "",
        blocks: item.content?.blocks || [],
        bg_color: item.content?.bg_color || "#ffffff",
        text_color: item.content?.text_color || "#0b0811",
      });
      setEditorMode("email_template");
      return;
    }
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (dialogType === "offer") { await handleSaveOffer(); return; }
    setSaving(true);
    try {
      if (dialogType === "service") {
        if (editingId) await api.put(`/catalog/services/${editingId}`, form);
        else await api.post("/catalog/services", form);
      } else if (dialogType === "acc_template") {
        const payload = { ...form, phases };
        if (editingId) await api.put(`/accompaniment-templates/${editingId}`, payload);
        else await api.post("/accompaniment-templates", payload);
      } else if (dialogType === "doc_template") {
        const payload = { ...form, fields };
        if (editingId) await api.put(`/catalog/document-templates/${editingId}`, payload);
        else await api.post("/catalog/document-templates", payload);
      } else if (dialogType === "inv_template" || dialogType === "email_template") {
        if (editingId) await api.put(`/facturation/templates/${editingId}`, form);
        else await api.post("/facturation/templates", form);
      } else if (dialogType === "questionnaire") {
        const payload = { ...form, fields, conditions: qstConditions };
        if (editingId) await api.put(`/prospect-questionnaires/${editingId}`, payload);
        else await api.post("/prospect-questionnaires", payload);
      }
      setShowDialog(false);
      setEditorMode(null);
      loadAll();
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const handleDelete = async (type, id) => {
    try {
      if (type === "service") await api.delete(`/catalog/services/${id}`);
      else if (type === "acc_template") await api.delete(`/accompaniment-templates/${id}`);
      else if (type === "doc_template") await api.delete(`/catalog/document-templates/${id}`);
      else if (type === "inv_template" || type === "email_template") await api.delete(`/facturation/templates/${id}`);
      else if (type === "questionnaire") await api.delete(`/prospect-questionnaires/${id}`);
      else if (type === "offer") await handleDeleteOffer(id);
      loadAll();
    } catch (e) { console.error(e); }
  };

  const handleAIGenerate = async () => {
    setGeneratingAI(true);
    try {
      const r = await api.post("/catalog/document-templates/generate-ai", { description: aiPrompt });
      setForm({ name: r.data.name || "", description: r.data.description || "", category: r.data.category || "" });
      setFields(r.data.fields || []);
      setShowAI(false);
      setDialogType("doc_template");
      setEditingId(null);
      setShowDialog(true);
    } catch (e) { console.error(e); }
    setGeneratingAI(false);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = file.name.split(".").pop()?.toLowerCase();
    if (!["docx", "pdf"].includes(ext)) {
      alert("Format non supporté. Utilisez DOCX ou PDF.");
      return;
    }
    setUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const r = await api.post("/catalog/document-templates/upload-file", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm({ name: r.data.name || "", description: r.data.description || "", category: r.data.category || "" });
      setFields(r.data.fields || []);
      setDialogType("doc_template");
      setEditingId(null);
      setShowDialog(true);
    } catch (e) {
      console.error(e);
      alert("Erreur lors de l'analyse du fichier.");
    }
    setUploadingFile(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleAccAIGenerate = async () => {
    setGeneratingAccAI(true);
    try {
      const r = await api.post("/catalog/accompaniment-templates/generate-ai", { description: accAiPrompt });
      const data = r.data;
      setForm({
        title: data.title || "", description: data.description || "",
        formula: data.formula || "START", price: data.price || "",
        duration_months: data.duration_months || 0,
      });
      setPhases(data.phases || []);
      setShowAccAI(false);
      setDialogType("acc_template");
      setEditingId(null);
      setShowDialog(true);
    } catch (e) { console.error(e); alert("Erreur lors de la generation IA."); }
    setGeneratingAccAI(false);
  };

  const handleAccFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = file.name.split(".").pop()?.toLowerCase();
    if (!["docx", "pdf"].includes(ext)) {
      alert("Format non supporte. Utilisez DOCX ou PDF.");
      return;
    }
    setUploadingAccFile(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const r = await api.post("/catalog/accompaniment-templates/upload-file", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const data = r.data;
      setForm({
        title: data.title || "", description: data.description || "",
        formula: data.formula || "START", price: data.price || "",
        duration_months: data.duration_months || 0,
      });
      setPhases(data.phases || []);
      setDialogType("acc_template");
      setEditingId(null);
      setShowDialog(true);
    } catch (e) {
      console.error(e);
      alert("Erreur lors de l'analyse du fichier.");
    }
    setUploadingAccFile(false);
    if (accFileInputRef.current) accFileInputRef.current.value = "";
  };

  const handleSvcAIGenerate = async () => {
    setGeneratingSvcAI(true);
    try {
      const r = await api.post("/catalog/services/generate-ai", { description: svcAiPrompt, kind: svcAiKind });
      const data = r.data;
      setForm({ name: data.name || "", code: data.code || "", description: data.description || "", category: data.category || "", price: data.price || "", duration: data.duration || "", fiche_info: data.fiche_info || null, cahier_des_charges: data.cahier_des_charges || null });
      setShowSvcAI(false);
      setDialogType("service");
      setEditingId(null);
      setShowDialog(true);
    } catch (e) { console.error(e); alert("Erreur lors de la generation IA."); }
    setGeneratingSvcAI(false);
  };

  const handleSvcFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = file.name.split(".").pop()?.toLowerCase();
    if (!["docx", "pdf"].includes(ext)) { alert("Format non supporte. DOCX ou PDF."); return; }
    setUploadingSvcFile(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const r = await api.post(`/catalog/services/upload-file?kind=${svcAiKind}`, formData, { headers: { "Content-Type": "multipart/form-data" } });
      const data = r.data;
      setForm({ name: data.name || "", code: data.code || "", description: data.description || "", category: data.category || "", price: data.price || "", duration: data.duration || "", fiche_info: data.fiche_info || null });
      setDialogType("service");
      setEditingId(null);
      setShowDialog(true);
    } catch (e) { console.error(e); alert("Erreur lors de l'analyse du fichier."); }
    setUploadingSvcFile(false);
    if (svcFileInputRef.current) svcFileInputRef.current.value = "";
  };

  // Questionnaire AI generation
  const handleQstAIGenerate = async () => {
    setGeneratingQstAI(true);
    try {
      const r = await api.post("/prospect-questionnaires/generate-ai", { description: qstAiPrompt });
      const data = r.data;
      setForm({ name: data.name || "", description: data.description || "", status: "active" });
      setFields(data.fields || []);
      setQstConditions(data.conditions || []);
      setShowQstAI(false);
      setDialogType("questionnaire");
      setEditingId(null);
      setShowDialog(true);
    } catch (e) { console.error(e); alert("Erreur lors de la generation IA."); }
    setGeneratingQstAI(false);
  };

  const handleQstFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = file.name.split(".").pop()?.toLowerCase();
    if (!["docx", "pdf", "csv", "txt"].includes(ext)) { alert("Format non supporte. DOCX, PDF ou CSV."); return; }
    setUploadingQstFile(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const r = await api.post("/prospect-questionnaires/upload-file", formData, { headers: { "Content-Type": "multipart/form-data" } });
      const data = r.data;
      setForm({ name: data.name || "", description: data.description || "", status: "active" });
      setFields(data.fields || []);
      setQstConditions(data.conditions || []);
      setDialogType("questionnaire");
      setEditingId(null);
      setShowDialog(true);
    } catch (e) { console.error(e); alert("Erreur lors de l'analyse du fichier."); }
    setUploadingQstFile(false);
    if (qstFileInputRef.current) qstFileInputRef.current.value = "";
  };


  // Phase editor for acc templates
  const addPhase = () => setPhases([...phases, { name: `Phase ${phases.length + 1}`, period: "", duration: "", tasks: [{ id: `t_${Date.now()}`, label: "", responsible: "Blitzz", deadline: "", status: "todo" }] }]);
  const removePhase = (i) => setPhases(phases.filter((_, idx) => idx !== i));
  const addTask = (pi) => { const u = [...phases]; u[pi].tasks.push({ id: `t_${Date.now()}`, label: "", responsible: "Blitzz", deadline: "", status: "todo" }); setPhases(u); };
  const removeTask = (pi, ti) => { const u = [...phases]; u[pi].tasks = u[pi].tasks.filter((_, i) => i !== ti); setPhases(u); };

  // Field editor for doc templates
  const addField = () => setFields([...fields, { id: `f_${Date.now()}`, field_id: `f_${Date.now()}`, label: "", type: "text", required: false, placeholder: "", options: [], section: "" }]);
  const removeField = (i) => setFields(fields.filter((_, idx) => idx !== i));

  // Condition editor for questionnaires
  const addCondition = () => setQstConditions([...qstConditions, { condition_id: `c_${Date.now()}`, if_field_id: "", operator: "equals", value: "", then_show_fields: [] }]);
  const removeCondition = (i) => setQstConditions(qstConditions.filter((_, idx) => idx !== i));

  // Tags CRUD
  const handleCreateTag = async () => {
    if (!newTag.key || !newTag.label) return;
    setSavingTag(true);
    try {
      await api.post("/builder/tags", newTag);
      setNewTag({ key: "", label: "", category: "entreprise" });
      const res = await api.get("/builder/tags");
      setTags(res.data?.tags || []);
    } catch (e) { console.error(e); alert(e.response?.data?.detail || "Erreur"); }
    setSavingTag(false);
  };
  const handleUpdateTag = async (tagId, data) => {
    try {
      await api.put(`/builder/tags/${tagId}`, data);
      const res = await api.get("/builder/tags");
      setTags(res.data?.tags || []);
      setEditingTag(null);
    } catch (e) { console.error(e); }
  };
  const handleDeleteTag = async (tagId) => {
    try {
      await api.delete(`/builder/tags/${tagId}`);
      const res = await api.get("/builder/tags");
      setTags(res.data?.tags || []);
    } catch (e) { console.error(e); alert(e.response?.data?.detail || "Erreur"); }
  };

  // Offers CRUD
  const openCreateOffer = () => {
    setDialogType("offer");
    setEditingId(null);
    setForm({
      name: "", description: "", modules: [],
      price_monthly: 0, price_yearly: 0,
      max_users: 5, max_prospects: 100, features: [],
      multi_societe_enabled: false,
      multi_societe_included: 1,
      multi_societe_fee_monthly: 0,
      multi_societe_fee_yearly: 0,
      multi_societe_max: 0,
      extra_fees: [],
      included_addon_ids: [],
    });
    setShowDialog(true);
  };
  const openEditOffer = (offer) => {
    setDialogType("offer");
    setEditingId(offer.offer_id);
    setForm({ ...offer });
    setShowDialog(true);
  };
  const handleSaveOffer = async () => {
    setSaving(true);
    try {
      if (editingId) await api.put(`/tenant-offers/${editingId}`, form);
      else await api.post("/tenant-offers", form);
      setShowDialog(false);
      loadAll();
    } catch (e) { console.error(e); }
    setSaving(false);
  };
  const handleDeleteOffer = async (offerId) => {
    try { await api.delete(`/tenant-offers/${offerId}`); loadAll(); } catch (e) { console.error(e); }
  };

  if (loading) return <div className="p-8 text-slate-400">Chargement...</div>;

  // ═══ FULLSCREEN ADVANCED EDITOR MODE ═══
  if (editorMode) {
    const editorTitle = editorMode === "inv_template" ? "Editeur Factures / Devis" : editorMode === "email_template" ? "Editeur Emails" : "Editeur Documents / Contrats";
    return (
      <div className="fixed inset-0 z-50 bg-[#0d0a14] flex flex-col" data-testid="fullscreen-editor">
        {/* Top bar */}
        <div className="flex items-center gap-3 px-4 py-2.5 border-b border-white/10 bg-[#161020] flex-shrink-0">
          <button onClick={() => setEditorMode(null)} className="flex items-center gap-1.5 text-slate-400 hover:text-white transition-colors text-sm" data-testid="editor-back-btn">
            <ArrowLeft className="w-4 h-4" /> Retour
          </button>
          <div className="w-px h-5 bg-white/10" />
          <span className="text-sm font-bold text-white">{editorTitle}</span>
          <span className="text-xs text-slate-500">{editingId ? "Modification" : "Nouveau"}</span>
          <div className="flex-1" />
          <Button onClick={handleSave} disabled={saving} className="bg-[#f7c935] text-[#0b0811] hover:bg-[#e6b825] font-bold h-8 text-xs px-4" data-testid="editor-save-btn">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
            {editingId ? "Enregistrer" : "Creer"}
          </Button>
        </div>
        {/* Editor body */}
        <div className="flex-1 overflow-hidden">
          {editorMode === "inv_template" && <InvoiceEditor form={form} setForm={setForm} tags={tags} />}
          {editorMode === "email_template" && <EmailEditor form={form} setForm={setForm} tags={tags} />}
          {editorMode === "doc_template" && <DocumentEditor form={form} setForm={setForm} fields={fields} setFields={setFields} tags={tags} />}
        </div>
      </div>
    );
  }

  const isTenantUser = isTenantCtx;

  const CATS = {
    services: { items: catalogServices, icon: Briefcase, label: "Services Blitzz", idKey: "catalog_id", nameKey: "name", group: "blitzz", type: "service" },
    acc_templates: { items: accTemplates, icon: Route, label: "Accompagnements", idKey: "template_id", nameKey: "title", group: "blitzz", type: "acc_template" },
    questionnaires: { items: questionnaires, icon: ClipboardList, label: "Questionnaires", idKey: "questionnaire_id", nameKey: "name", group: "crm", type: "questionnaire" },
    doc_templates: { items: docTemplates, icon: FileText, label: "Documents & Contrats", idKey: "template_id", nameKey: "name", group: "docs", type: "doc_template" },
    inv_templates: { items: invoiceTemplates, icon: Receipt, label: "Facturation & Devis", idKey: "template_id", nameKey: "name", group: "invoice", type: "inv_template" },
    email_templates: { items: emailTemplates, icon: Mail, label: "Modeles Mails", idKey: "template_id", nameKey: "name", group: "email", type: "email_template" },
    offers: { items: offers, icon: Package, label: "Offres Multi-Tenant", idKey: "offer_id", nameKey: "name", group: "tenant", type: "offer", hidden: true },
    balises: { items: tags, icon: Tag, label: "Gestion des Balises", idKey: "tag_id", nameKey: "label", group: "config", type: "balise" },
    routine_pdf: { items: [], icon: FileText, label: "Config PDF Routines", idKey: "id", nameKey: "name", group: "config", type: "routine_pdf" },
  };

  const ALL_GROUPS = [
    { key: "blitzz", label: "Builder Services Blitzz", tabs: ["services", "acc_templates"] },
    { key: "crm", label: "CRM & Prospection", tabs: ["questionnaires"] },
    { key: "docs", label: "Modeles Documents & Contrats", tabs: ["doc_templates"] },
    { key: "invoice", label: "Modeles Facturation & Devis", tabs: ["inv_templates"] },
    { key: "email", label: "Modeles Mails", tabs: ["email_templates"] },
    { key: "config", label: "Configuration & IA", tabs: ["balises", "routine_pdf"] },
  ];

  // Tenant sees only their platform tools, Admin sees everything
  const TENANT_GROUPS = ["crm", "docs", "invoice", "email", "config"];
  const GROUPS = isTenantUser
    ? ALL_GROUPS.filter(g => TENANT_GROUPS.includes(g.key))
    : ALL_GROUPS;

  const curr = CATS[tab];
  const filtered = curr.items.filter(i => (i[curr.nameKey] || "").toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="p-8 max-w-6xl" data-testid="builder-page">
      <div className="flex items-start justify-between mb-8">
        <div>
          <h1 className="text-4xl font-black tracking-tighter"><span style={{ color: "#f7c935" }}>{isTenantUser ? t("builder.my_builder") : t("builder.title")}</span></h1>
          <p className="text-slate-400 mt-2">{t("builder.subtitle")}</p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        {/* Grouped tabs */}
        <div className="space-y-4 mb-6">
          {GROUPS.map(group => (
            <div key={group.key}>
              <div className="text-[10px] uppercase tracking-wider text-slate-600 font-bold mb-1.5 px-1">{group.label}</div>
              <div className="flex gap-1.5 flex-wrap">
                {group.tabs.map(k => {
                  const v = CATS[k];
                  return (
                    <button key={k} onClick={() => setTab(k)}
                      className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-colors ${tab === k ? "bg-[#f7c935] text-[#14101c]" : "bg-[#14101c] text-slate-400 border border-white/5 hover:text-white hover:border-white/10"}`}
                      data-testid={`builder-tab-${k}`}>
                      <v.icon className="w-3.5 h-3.5" /> {v.label} <span className="opacity-60">({v.items.length})</span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {tab !== "balises" && tab !== "routine_pdf" && (
        <div className="flex items-center gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <Input placeholder="Rechercher..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 bg-[#0b0811] border-white/10 text-white placeholder:text-slate-500 focus:border-[#f7c935]" />
          </div>
          {tab === "services" && (
            <>
              <input type="file" ref={svcFileInputRef} accept=".docx,.pdf" onChange={handleSvcFileUpload} className="hidden" data-testid="svc-file-upload-input" />
              <Button onClick={() => svcFileInputRef.current?.click()} disabled={uploadingSvcFile} className="bg-[#14101c] border border-white/10 text-white hover:bg-white/5 font-bold" data-testid="upload-svc-file-btn">
                {uploadingSvcFile ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                {uploadingSvcFile ? "Analyse..." : "Importer DOCX/PDF"}
              </Button>
              <Button onClick={() => { setSvcAiPrompt(""); setSvcAiKind("fiche_info"); setShowSvcAI(true); }} className="bg-[#461d8a] text-white hover:bg-[#5a2d9c] font-bold" data-testid="ai-generate-svc-btn">
                <Brain className="w-4 h-4 mr-2" /> Generer par IA
              </Button>
            </>
          )}
          {tab === "doc_templates" && (
            <>
              <input type="file" ref={fileInputRef} accept=".docx,.pdf" onChange={handleFileUpload} className="hidden" data-testid="file-upload-input" />
              <Button onClick={() => fileInputRef.current?.click()} disabled={uploadingFile} className="bg-[#14101c] border border-white/10 text-white hover:bg-white/5 font-bold" data-testid="upload-file-btn">
                {uploadingFile ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                {uploadingFile ? "Analyse..." : "Importer DOCX/PDF"}
              </Button>
              <Button onClick={() => { setAiPrompt(""); setShowAI(true); }} className="bg-[#461d8a] text-white hover:bg-[#5a2d9c] font-bold" data-testid="ai-generate-template-btn">
                <Brain className="w-4 h-4 mr-2" /> Generer par IA
              </Button>
            </>
          )}
          {tab === "acc_templates" && (
            <>
              <input type="file" ref={accFileInputRef} accept=".docx,.pdf" onChange={handleAccFileUpload} className="hidden" data-testid="acc-file-upload-input" />
              <Button onClick={() => accFileInputRef.current?.click()} disabled={uploadingAccFile} className="bg-[#14101c] border border-white/10 text-white hover:bg-white/5 font-bold" data-testid="upload-acc-file-btn">
                {uploadingAccFile ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                {uploadingAccFile ? "Analyse..." : "Importer DOCX/PDF"}
              </Button>
              <Button onClick={() => { setAccAiPrompt(""); setShowAccAI(true); }} className="bg-[#461d8a] text-white hover:bg-[#5a2d9c] font-bold" data-testid="ai-generate-acc-btn">
                <Brain className="w-4 h-4 mr-2" /> Generer par IA
              </Button>
            </>
          )}
          {tab === "questionnaires" && (
            <>
              <input type="file" ref={qstFileInputRef} accept=".docx,.pdf,.csv,.txt" onChange={handleQstFileUpload} className="hidden" data-testid="qst-file-upload-input" />
              <Button onClick={() => qstFileInputRef.current?.click()} disabled={uploadingQstFile} className="bg-[#14101c] border border-white/10 text-white hover:bg-white/5 font-bold" data-testid="upload-qst-file-btn">
                {uploadingQstFile ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                {uploadingQstFile ? "Analyse..." : "Importer fichier"}
              </Button>
              <Button onClick={() => { setQstAiPrompt(""); setShowQstAI(true); }} className="bg-[#461d8a] text-white hover:bg-[#5a2d9c] font-bold" data-testid="ai-generate-qst-btn">
                <Brain className="w-4 h-4 mr-2" /> Generer par IA
              </Button>
            </>
          )}
          <Button onClick={() => openCreate(CATS[tab]?.type || "service")} className="bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825] font-bold" data-testid="builder-create-btn">
            <Plus className="w-4 h-4 mr-2" /> Creer
          </Button>
        </div>
        )}

        {/* ═══ TAGS MANAGEMENT TAB ═══ */}
        {tab === "balises" && <BalisesTab tags={tags} tagCategories={tagCategories} newTag={newTag} setNewTag={setNewTag} editingTag={editingTag} setEditingTag={setEditingTag} savingTag={savingTag} handleCreateTag={handleCreateTag} handleUpdateTag={handleUpdateTag} handleDeleteTag={handleDeleteTag} search={search} setSearch={setSearch} />}

        {/* ═══ ROUTINE PDF CONFIG TAB ═══ */}
        {tab === "routine_pdf" && <RoutinePdfConfigTab api={api} />}

        {/* Items List (non-special tabs) */}
        {tab !== "balises" && tab !== "routine_pdf" && (
        <div className="space-y-3">
          {filtered.length === 0 ? (
            <div className="bg-[#14101c] border border-white/10 rounded-xl p-16 text-center">
              <curr.icon className="w-12 h-12 mx-auto mb-3 text-slate-600" />
              <p className="text-slate-500">Aucun element. Cliquez sur "Creer" pour commencer.</p>
            </div>
          ) : filtered.map((item, i) => (
            <div key={item[curr.idKey] || `${curr.type}-${i}`} className="bg-[#14101c] border border-white/10 rounded-xl p-5 flex items-center justify-between animate-fade-in-up group" style={{ animationDelay: `${i * 0.03}s` }} data-testid={`builder-item-${item[curr.idKey] || i}`}>
              <div className="flex items-center gap-4 flex-1 min-w-0">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: "#461d8a20", color: "#5a2d9c" }}>
                  <curr.icon className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <div className="font-semibold truncate flex items-center gap-2">
                    {item[curr.nameKey]}
                    {(tab === "inv_templates" || tab === "email_templates") && item.is_default && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#f7c935]/20 text-[#f7c935] font-bold flex items-center gap-0.5"><Star className="w-2.5 h-2.5" /> Defaut</span>
                    )}
                  </div>
                  <div className="text-sm text-slate-500 truncate">
                    {tab === "services" && `${item.code || ""} ${item.category ? `- ${item.category}` : ""} ${item.price ? `- ${item.price}` : ""}`}
                    {tab === "acc_templates" && `${item.formula || ""} - ${item.duration_months ? `${item.duration_months} mois` : ""} ${item.price ? `- ${item.price}` : ""} - ${item.phases?.length || 0} phases`}
                    {tab === "doc_templates" && `${item.category || ""} - ${item.fields?.length || 0} champs`}
                    {tab === "inv_templates" && `${item.type || ""} ${item.content?.cgv ? "- CGV" : ""} ${item.content?.mentions_legales ? "- Mentions" : ""}`}
                    {tab === "email_templates" && `${item.content?.email_subject || "Pas d'objet"}`}
                    {tab === "questionnaires" && `${item.fields?.length || 0} champs, ${item.conditions?.length || 0} conditions`}
                    {tab === "offers" && `${item.price_monthly}EUR/mois - ${(item.modules || []).length} modules - Max ${item.max_users} users${item.multi_societe_enabled ? ` - Multi-societe (${item.multi_societe_included || 1} inc. / +${item.multi_societe_fee_monthly || 0}EUR)` : ""}${(item.extra_fees || []).length ? ` - ${(item.extra_fees || []).length} frais` : ""}`}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <button onClick={() => openEdit(curr.type, item)} className="p-1.5 rounded hover:bg-white/10">
                  <Pencil className="w-4 h-4 text-slate-400" />
                </button>
                <button onClick={() => handleDelete(curr.type, item[curr.idKey])} className="p-1.5 rounded hover:bg-red-500/10">
                  <Trash2 className="w-4 h-4 text-red-400" />
                </button>
              </div>
            </div>
          ))}
        </div>
        )}
      </Tabs>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={(v) => { setShowDialog(v); if (!v) setEditingId(null); }}>
        <DialogContent className="bg-[#14101c] border-white/10 text-white max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">
              {editingId ? "Modifier" : "Creer"} -
              {dialogType === "service" && " Service"}
              {dialogType === "acc_template" && " Modele Accompagnement"}
              {dialogType === "doc_template" && " Modele Document"}
              {dialogType === "inv_template" && " Modele Facturation"}
              {dialogType === "email_template" && " Modele Email"}
              {dialogType === "questionnaire" && " Questionnaire Prospects"}
              {dialogType === "offer" && " Offre Multi-Tenant"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {/* Service fields */}
            {dialogType === "service" && (<>
              <div className="grid grid-cols-2 gap-3">
                <Input placeholder="Nom du service" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="builder-service-name" />
                <Input placeholder="Code (ex: CS1)" value={form.code || ""} onChange={(e) => setForm({ ...form, code: e.target.value })} className={inputCls} />
              </div>
              <Textarea placeholder="Description" value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className={inputCls} />
              <div className="grid grid-cols-3 gap-3">
                <Input placeholder="Categorie" value={form.category || ""} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inputCls} />
                <Input placeholder="Prix" value={form.price || ""} onChange={(e) => setForm({ ...form, price: e.target.value })} className={inputCls} />
                <Input placeholder="Duree" value={form.duration || ""} onChange={(e) => setForm({ ...form, duration: e.target.value })} className={inputCls} />
              </div>
            </>)}

            {/* Acc Template fields */}
            {dialogType === "acc_template" && (<>
              <Input placeholder="Titre du modele" value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} data-testid="builder-template-title" />
              <Textarea placeholder="Description" value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className={inputCls} />
              <div className="grid grid-cols-3 gap-3">
                <Select value={form.formula || "START"} onValueChange={(v) => setForm({ ...form, formula: v })}>
                  <SelectTrigger className={inputCls}><SelectValue placeholder="Formule" /></SelectTrigger>
                  <SelectContent className="bg-[#14101c] border-white/10 text-white">
                    <SelectItem value="START">START</SelectItem><SelectItem value="PLUS">PLUS</SelectItem>
                    <SelectItem value="SMART">SMART</SelectItem><SelectItem value="BLITZ">BLITZ</SelectItem>
                  </SelectContent>
                </Select>
                <Input placeholder="Duree (mois)" value={form.duration_months || ""} onChange={(e) => setForm({ ...form, duration_months: e.target.value })} className={inputCls} />
                <Input placeholder="Prix (EUR)" value={form.price || ""} onChange={(e) => setForm({ ...form, price: e.target.value })} className={inputCls} />
              </div>

              <div className="border-t border-white/10 pt-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-bold text-sm" style={{ color: "#f7c935" }}>Phases & Taches</h4>
                  <Button size="sm" onClick={addPhase} variant="outline" className="border-white/10 text-white hover:bg-white/5 text-xs"><Plus className="w-3 h-3 mr-1" /> Phase</Button>
                </div>
                {phases.map((phase, pi) => (
                  <div key={pi} className="bg-[#0b0811] border border-white/5 rounded-lg p-4 mb-3">
                    <div className="flex items-center gap-2 mb-3">
                      <Input value={phase.name} onChange={(e) => { const u = [...phases]; u[pi].name = e.target.value; setPhases(u); }} className="bg-[#14101c] border-white/10 text-white text-sm h-9" />
                      <Input value={phase.period || ""} onChange={(e) => { const u = [...phases]; u[pi].period = e.target.value; setPhases(u); }} placeholder="Periode" className="bg-[#14101c] border-white/10 text-white text-sm h-9 w-28" />
                      <Input value={phase.duration || ""} onChange={(e) => { const u = [...phases]; u[pi].duration = e.target.value; setPhases(u); }} placeholder="Duree" className="bg-[#14101c] border-white/10 text-white text-sm h-9 w-28" />
                      {phases.length > 1 && <button onClick={() => removePhase(pi)} className="p-1 hover:bg-red-500/10 rounded"><Trash2 className="w-4 h-4 text-red-400" /></button>}
                    </div>
                    <div className="space-y-2 ml-2">
                      {phase.tasks?.map((task, ti) => (
                        <div key={ti} className="flex items-center gap-2">
                          <Input value={task.label} onChange={(e) => { const u = [...phases]; u[pi].tasks[ti].label = e.target.value; setPhases(u); }} placeholder="Tache..." className="bg-[#14101c] border-white/10 text-white text-xs h-8" />
                          <Input value={task.responsible || ""} onChange={(e) => { const u = [...phases]; u[pi].tasks[ti].responsible = e.target.value; setPhases(u); }} placeholder="Resp." className="bg-[#14101c] border-white/10 text-white text-xs h-8 w-24" />
                          {phase.tasks.length > 1 && <button onClick={() => removeTask(pi, ti)} className="p-0.5 hover:bg-red-500/10 rounded"><Trash2 className="w-3.5 h-3.5 text-red-400" /></button>}
                        </div>
                      ))}
                      <button onClick={() => addTask(pi)} className="text-xs font-medium flex items-center gap-1 mt-1" style={{ color: "#f7c935" }}><Plus className="w-3 h-3" /> Tache</button>
                    </div>
                  </div>
                ))}
              </div>
            </>)}

            {/* Doc Template fields - Advanced 3-Section Editor */}
            {dialogType === "doc_template" && (<>
              <Input placeholder="Nom du modele" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="builder-doc-template-name" />
              <Textarea placeholder="Description" value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className={inputCls} />
              <div className="grid grid-cols-2 gap-3">
                <Input placeholder="Categorie (ex: Convention, Contrat, PV)" value={form.category || ""} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inputCls} />
                <div className="flex items-center gap-2 pt-1">
                  <input type="color" value={form.primary_color || "#461d8a"} onChange={(e) => setForm({ ...form, primary_color: e.target.value })} className="w-7 h-7 rounded border border-white/10 cursor-pointer" />
                  <span className="text-xs text-slate-500">Couleur du document</span>
                </div>
              </div>

              {/* 3-Section Structure */}
              <div className="border border-white/10 rounded-xl overflow-hidden" data-testid="doc-3section-editor">
                <div className="bg-[#f7c935]/5 border-b border-white/10 px-4 py-2 flex items-center gap-2">
                  <FileText className="w-3.5 h-3.5 text-[#f7c935]" />
                  <span className="text-[10px] font-bold text-[#f7c935] uppercase tracking-wider">En-tete du document</span>
                </div>
                <div className="p-4">
                  <Textarea value={form.doc_header || ""} onChange={(e) => setForm({ ...form, doc_header: e.target.value })} rows={3} placeholder="Logo, titre du document, references, date...&#10;&#10;Ex: CONVENTION DE FORMATION PROFESSIONNELLE N{reference}" className={inputCls} />
                  <div className="flex flex-wrap gap-1 mt-2">
                    {["{company_name}", "{company_address}", "{company_siret}", "{reference}", "{date_today}", "{logo_url}"].map(v => (
                      <button key={v} type="button" onClick={() => setForm({ ...form, doc_header: (form.doc_header || "") + " " + v })}
                        className="text-[9px] px-2 py-0.5 rounded-full bg-[#f7c935]/10 text-[#f7c935] hover:bg-[#f7c935]/20 transition-colors border border-[#f7c935]/20">{v}</button>
                    ))}
                  </div>
                </div>

                <div className="bg-blue-500/5 border-y border-white/10 px-4 py-2 flex items-center gap-2">
                  <FileText className="w-3.5 h-3.5 text-blue-400" />
                  <span className="text-[10px] font-bold text-blue-400 uppercase tracking-wider">Corps - Champs du formulaire ({fields.length})</span>
                  <div className="ml-auto">
                    <Button size="sm" onClick={addField} variant="outline" className="border-white/10 text-white hover:bg-white/5 text-[10px] h-6 px-2"><Plus className="w-3 h-3 mr-0.5" /> Champ</Button>
                  </div>
                </div>
                <div className="p-4">
                  <div className="space-y-2 max-h-[250px] overflow-y-auto">
                    {fields.map((field, fi) => (
                      <div key={fi} className="bg-[#0b0811] border border-white/5 rounded-lg p-3">
                        <div className="flex items-center gap-2 mb-2">
                          <Input value={field.label} onChange={(e) => { const u = [...fields]; u[fi].label = e.target.value; setFields(u); }} placeholder="Libelle" className="bg-[#14101c] border-white/10 text-white text-xs h-8 flex-1" />
                          <Select value={field.type} onValueChange={(v) => { const u = [...fields]; u[fi].type = v; setFields(u); }}>
                            <SelectTrigger className="bg-[#14101c] border-white/10 text-white text-xs h-8 w-28"><SelectValue /></SelectTrigger>
                            <SelectContent className="bg-[#14101c] border-white/10 text-white">
                              {["text", "textarea", "select", "checkbox", "date", "number", "email", "phone", "signature"].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                            </SelectContent>
                          </Select>
                          <button onClick={() => { const u = [...fields]; u[fi].section = u[fi].section === "header" ? "body" : u[fi].section === "body" ? "footer" : "header"; setFields(u); }}
                            className={`text-[9px] px-2 py-0.5 rounded-full ${field.section === "header" ? "bg-[#f7c935]/10 text-[#f7c935]" : field.section === "footer" ? "bg-green-500/10 text-green-400" : "bg-blue-500/10 text-blue-400"}`}>
                            {field.section === "header" ? "En-tete" : field.section === "footer" ? "Pied" : "Corps"}
                          </button>
                          <button onClick={() => { const u = [...fields]; u[fi].required = !u[fi].required; setFields(u); }}
                            className={`text-xs px-2 py-1 rounded ${field.required ? "bg-[#f7c935]/10 text-[#f7c935]" : "bg-white/5 text-slate-500"}`}>
                            {field.required ? "Requis" : "Opt."}
                          </button>
                          <button onClick={() => removeField(fi)} className="p-1 hover:bg-red-500/10 rounded"><Trash2 className="w-3.5 h-3.5 text-red-400" /></button>
                        </div>
                        {(field.type === "select" || field.type === "checkbox") && (
                          <Input value={(field.options || []).join(", ")} onChange={(e) => { const u = [...fields]; u[fi].options = e.target.value.split(",").map(s => s.trim()); setFields(u); }}
                            placeholder="Options (separees par virgule)" className="bg-[#14101c] border-white/10 text-white text-xs h-7" />
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-green-500/5 border-t border-white/10 px-4 py-2 flex items-center gap-2">
                  <FileText className="w-3.5 h-3.5 text-green-400" />
                  <span className="text-[10px] font-bold text-green-400 uppercase tracking-wider">Pied de page</span>
                </div>
                <div className="p-4">
                  <Textarea value={form.doc_footer || ""} onChange={(e) => setForm({ ...form, doc_footer: e.target.value })} rows={3} placeholder="Mentions legales, signatures, cachet...&#10;&#10;Fait a _______, le {date_today}&#10;Signature :" className={inputCls} />
                  <div className="flex flex-wrap gap-1 mt-2">
                    {["{date_today}", "{signature_client}", "{signature_blitzz}", "{company_name}"].map(v => (
                      <button key={v} type="button" onClick={() => setForm({ ...form, doc_footer: (form.doc_footer || "") + " " + v })}
                        className="text-[9px] px-2 py-0.5 rounded-full bg-green-500/10 text-green-400 hover:bg-green-500/20 transition-colors border border-green-500/20">{v}</button>
                    ))}
                  </div>
                </div>
              </div>
            </>)}

            {/* Invoice Template fields - Advanced 3-Section Editor */}
            {/* inv_template, email_template, doc_template now use fullscreen editors */}

            {/* Offer form */}
            {dialogType === "offer" && (<>
              <Input placeholder="Nom de l'offre" value={form.name || ""} onChange={e => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="offer-name" />
              <Textarea placeholder="Description" value={form.description || ""} onChange={e => setForm({ ...form, description: e.target.value })} rows={2} className={inputCls} />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] text-[#888194] mb-1 block">Prix mensuel (EUR)</label>
                  <Input type="number" value={form.price_monthly || 0} onChange={e => setForm({ ...form, price_monthly: parseFloat(e.target.value) || 0 })} className={inputCls} data-testid="offer-price-monthly" />
                </div>
                <div>
                  <label className="text-[10px] text-[#888194] mb-1 block">Prix annuel (EUR)</label>
                  <Input type="number" value={form.price_yearly || 0} onChange={e => setForm({ ...form, price_yearly: parseFloat(e.target.value) || 0 })} className={inputCls} data-testid="offer-price-yearly" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] text-[#888194] mb-1 block">Max utilisateurs</label>
                  <Input type="number" value={form.max_users || 5} onChange={e => setForm({ ...form, max_users: parseInt(e.target.value) || 5 })} className={inputCls} />
                </div>
                <div>
                  <label className="text-[10px] text-[#888194] mb-1 block">Max prospects</label>
                  <Input type="number" value={form.max_prospects || 100} onChange={e => setForm({ ...form, max_prospects: parseInt(e.target.value) || 100 })} className={inputCls} />
                </div>
              </div>
              <div>
                <label className="text-[10px] text-[#888194] mb-2 block">Modules inclus</label>
                <div className="mb-3">
                  <div className="text-[9px] uppercase tracking-wider text-[#8b5cf6] font-bold mb-1.5">Mon Espace</div>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { key: "espace_taches", label: "Mes Taches" },
                      { key: "espace_equipement", label: "Mon Equipement" },
                      { key: "espace_depenses", label: "Mes Depenses" },
                      { key: "espace_documents", label: "Mes Documents" },
                      { key: "messages", label: "Messages" },
                      { key: "agenda", label: "Agenda" },
                    ].map(m => {
                      const isSelected = (form.modules || []).includes(m.key);
                      return (
                        <button key={m.key} type="button" onClick={() => {
                          const mods = form.modules || [];
                          setForm({ ...form, modules: mods.includes(m.key) ? mods.filter(x => x !== m.key) : [...mods, m.key] });
                        }}
                          className={`text-[10px] px-3 py-1.5 rounded-lg font-semibold transition-colors border ${isSelected ? "bg-[#461d8a]/15 text-[#8b5cf6] border-[#461d8a]/40" : "bg-white/3 text-[#888194] border-white/10 hover:border-white/20"}`}
                          data-testid={`offer-module-${m.key}`}>
                          {m.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <div className="text-[9px] uppercase tracking-wider text-[#f7c935] font-bold mb-1.5">Autres modules</div>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { key: "prospects", label: "Prospects" },
                      { key: "crm", label: "CRM" },
                      { key: "marketing", label: "Marketing" },
                      { key: "builder", label: "Builder" },
                      { key: "facturation", label: "Facturation" },
                      { key: "agents_ia", label: "Agents IA" },
                    ].map(m => {
                      const isSelected = (form.modules || []).includes(m.key);
                      return (
                        <button key={m.key} type="button" onClick={() => {
                          const mods = form.modules || [];
                          setForm({ ...form, modules: mods.includes(m.key) ? mods.filter(x => x !== m.key) : [...mods, m.key] });
                        }}
                          className={`text-[10px] px-3 py-1.5 rounded-lg font-semibold transition-colors border ${isSelected ? "bg-[#f7c935]/15 text-[#f7c935] border-[#f7c935]/30" : "bg-white/3 text-[#888194] border-white/10 hover:border-white/20"}`}
                          data-testid={`offer-module-${m.key}`}>
                          {m.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <div className="text-[9px] uppercase tracking-wider text-[#f7c935] font-bold mb-1.5">Formation</div>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { key: "formation_bibliotheque", label: "Bibliotheque" },
                      { key: "formation_lms", label: "LMS & Sessions" },
                      { key: "formation_evaluations", label: "Evaluations" },
                      { key: "formation_catalogue", label: "Catalogue public" },
                      { key: "formation_edof", label: "Export EDOF" },
                      { key: "qualiopi", label: "Qualiopi" },
                    ].map(m => {
                      const isSelected = (form.modules || []).includes(m.key);
                      return (
                        <button key={m.key} type="button" onClick={() => {
                          const mods = form.modules || [];
                          setForm({ ...form, modules: mods.includes(m.key) ? mods.filter(x => x !== m.key) : [...mods, m.key] });
                        }}
                          className={`text-[10px] px-3 py-1.5 rounded-lg font-semibold transition-colors border ${isSelected ? "bg-[#f7c935]/15 text-[#f7c935] border-[#f7c935]/30" : "bg-white/3 text-[#888194] border-white/10 hover:border-white/20"}`}
                          data-testid={`offer-module-${m.key}`}>
                          {m.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
              <div>
                <label className="text-[10px] text-[#888194] mb-1 block">Fonctionnalites (1 par ligne)</label>
                <Textarea value={(form.features || []).join("\n")} onChange={e => setForm({ ...form, features: e.target.value.split("\n").filter(Boolean) })} rows={3} placeholder="CRM Pipeline&#10;Gestion Prospects&#10;Support prioritaire" className={inputCls} />
              </div>

              {/* Multi-societe configuration */}
              <div className="border-t border-white/10 pt-4 mt-2">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h4 className="font-bold text-sm" style={{ color: "#f7c935" }}>Mode Multi-societes</h4>
                    <p className="text-[10px] text-slate-500">Definissez le nombre de societes incluses et les frais par societe supplementaire</p>
                  </div>
                  <button type="button" onClick={() => setForm({ ...form, multi_societe_enabled: !form.multi_societe_enabled })}
                    className={`text-[10px] px-3 py-1.5 rounded-lg font-semibold transition-colors border ${form.multi_societe_enabled ? "bg-[#f7c935]/15 text-[#f7c935] border-[#f7c935]/30" : "bg-white/3 text-[#888194] border-white/10"}`}
                    data-testid="offer-multisoc-toggle">
                    {form.multi_societe_enabled ? "Active" : "Desactive"}
                  </button>
                </div>

                {form.multi_societe_enabled && (
                  <div className="bg-[#0b0811] border border-white/5 rounded-lg p-3 space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-[10px] text-[#888194] mb-1 block">Societes incluses</label>
                        <Input type="number" min="1" value={form.multi_societe_included ?? 1} onChange={e => setForm({ ...form, multi_societe_included: parseInt(e.target.value) || 1 })} className={inputCls} data-testid="offer-multisoc-included" />
                      </div>
                      <div>
                        <label className="text-[10px] text-[#888194] mb-1 block">Max societes (0 = illimite)</label>
                        <Input type="number" min="0" value={form.multi_societe_max ?? 0} onChange={e => setForm({ ...form, multi_societe_max: parseInt(e.target.value) || 0 })} className={inputCls} data-testid="offer-multisoc-max" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-[10px] text-[#888194] mb-1 block">Frais / societe suppl. (mensuel EUR)</label>
                        <Input type="number" min="0" step="0.01" value={form.multi_societe_fee_monthly ?? 0} onChange={e => setForm({ ...form, multi_societe_fee_monthly: parseFloat(e.target.value) || 0 })} className={inputCls} data-testid="offer-multisoc-fee-monthly" />
                      </div>
                      <div>
                        <label className="text-[10px] text-[#888194] mb-1 block">Frais / societe suppl. (annuel EUR)</label>
                        <Input type="number" min="0" step="0.01" value={form.multi_societe_fee_yearly ?? 0} onChange={e => setForm({ ...form, multi_societe_fee_yearly: parseFloat(e.target.value) || 0 })} className={inputCls} data-testid="offer-multisoc-fee-yearly" />
                      </div>
                    </div>
                    <p className="text-[10px] text-slate-500 italic">Exemple : 1 societe incluse, 15 EUR/mois par societe supplementaire</p>
                  </div>
                )}
              </div>

              {/* Additional custom fees */}
              <div className="border-t border-white/10 pt-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h4 className="font-bold text-sm" style={{ color: "#f7c935" }}>Frais additionnels ({(form.extra_fees || []).length})</h4>
                    <p className="text-[10px] text-slate-500">Frais optionnels facturables (ex: utilisateur suppl., formation sur mesure, setup...)</p>
                  </div>
                  <Button size="sm" type="button" onClick={() => setForm({ ...form, extra_fees: [...(form.extra_fees || []), { label: "", unit: "unite", price_monthly: 0, price_yearly: 0 }] })}
                    variant="outline" className="border-white/10 text-white hover:bg-white/5 text-xs" data-testid="offer-add-fee-btn">
                    <Plus className="w-3 h-3 mr-1" /> Frais
                  </Button>
                </div>
                <div className="space-y-2">
                  {(form.extra_fees || []).map((fee, fi) => (
                    <div key={fi} className="bg-[#0b0811] border border-white/5 rounded-lg p-3" data-testid={`offer-fee-row-${fi}`}>
                      <div className="grid grid-cols-12 gap-2 items-center">
                        <Input value={fee.label || ""} onChange={e => { const u = [...(form.extra_fees || [])]; u[fi].label = e.target.value; setForm({ ...form, extra_fees: u }); }}
                          placeholder="Libelle (ex: Utilisateur supplementaire)" className={`${inputCls} col-span-5 text-xs h-8`} data-testid={`offer-fee-label-${fi}`} />
                        <Input value={fee.unit || ""} onChange={e => { const u = [...(form.extra_fees || [])]; u[fi].unit = e.target.value; setForm({ ...form, extra_fees: u }); }}
                          placeholder="Unite" className={`${inputCls} col-span-2 text-xs h-8`} data-testid={`offer-fee-unit-${fi}`} />
                        <Input type="number" min="0" step="0.01" value={fee.price_monthly ?? 0} onChange={e => { const u = [...(form.extra_fees || [])]; u[fi].price_monthly = parseFloat(e.target.value) || 0; setForm({ ...form, extra_fees: u }); }}
                          placeholder="Mensuel" className={`${inputCls} col-span-2 text-xs h-8`} data-testid={`offer-fee-monthly-${fi}`} />
                        <Input type="number" min="0" step="0.01" value={fee.price_yearly ?? 0} onChange={e => { const u = [...(form.extra_fees || [])]; u[fi].price_yearly = parseFloat(e.target.value) || 0; setForm({ ...form, extra_fees: u }); }}
                          placeholder="Annuel" className={`${inputCls} col-span-2 text-xs h-8`} data-testid={`offer-fee-yearly-${fi}`} />
                        <button type="button" onClick={() => setForm({ ...form, extra_fees: (form.extra_fees || []).filter((_, i) => i !== fi) })} className="col-span-1 p-1 hover:bg-red-500/10 rounded flex justify-center" data-testid={`offer-fee-remove-${fi}`}>
                          <Trash2 className="w-3.5 h-3.5 text-red-400" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Bundled addon modules included in this offer */}
              <div className="border-t border-white/10 pt-4">
                <div className="mb-2">
                  <h4 className="font-bold text-sm" style={{ color: "#f7c935" }}>Modules complementaires inclus ({(form.included_addon_ids || []).length})</h4>
                  <p className="text-[10px] text-slate-500">Ces modules sont automatiquement actives quand un tenant souscrit a cette offre (aucun surcout pour lui).</p>
                </div>
                <div className="bg-[#0b0811] border border-white/5 rounded-lg p-2 max-h-48 overflow-y-auto space-y-1">
                  {allAddons.length === 0 && <p className="text-[10px] text-slate-500 italic p-2">Aucun module complementaire cree. Allez dans Master Admin &gt; Modules complementaires.</p>}
                  {allAddons.map(a => {
                    const selected = (form.included_addon_ids || []).includes(a.addon_id);
                    return (
                      <button key={a.addon_id} type="button"
                        onClick={() => {
                          const ids = form.included_addon_ids || [];
                          setForm({ ...form, included_addon_ids: selected ? ids.filter(x => x !== a.addon_id) : [...ids, a.addon_id] });
                        }}
                        className={`w-full text-left px-2 py-1.5 rounded flex items-center gap-2 text-xs transition-colors ${selected ? "bg-[#f7c935]/10 border border-[#f7c935]/30" : "border border-white/5 hover:border-white/10"}`}
                        data-testid={`offer-bundle-addon-${a.addon_id}`}>
                        <div className={`w-3.5 h-3.5 rounded border flex items-center justify-center ${selected ? "bg-[#f7c935] border-[#f7c935]" : "border-white/20"}`}>
                          {selected && <span className="text-[#0b0811] font-black text-[10px]">✓</span>}
                        </div>
                        <span className="flex-1 truncate text-white">{a.name}</span>
                        <span className="text-[10px] text-slate-500">{a.price_monthly}EUR/mois</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </>)}

            {/* Questionnaire fields */}
            {dialogType === "questionnaire" && (<>
              <Input placeholder="Nom du questionnaire" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="builder-qst-name" />
              <Textarea placeholder="Description / objectif du questionnaire" value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className={inputCls} />

              {/* Fields editor */}
              <div className="border-t border-white/10 pt-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-bold text-sm" style={{ color: "#f7c935" }}>Champs du questionnaire ({fields.length})</h4>
                  <Button size="sm" onClick={addField} variant="outline" className="border-white/10 text-white hover:bg-white/5 text-xs"><Plus className="w-3 h-3 mr-1" /> Champ</Button>
                </div>
                <div className="space-y-2 max-h-[250px] overflow-y-auto">
                  {fields.map((field, fi) => (
                    <div key={fi} className="bg-[#0b0811] border border-white/5 rounded-lg p-3" data-testid={`qst-field-${fi}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <Input value={field.label || ""} onChange={(e) => { const u = [...fields]; u[fi].label = e.target.value; setFields(u); }} placeholder="Question / Libelle" className="bg-[#14101c] border-white/10 text-white text-xs h-8 flex-1" />
                        <Select value={field.type || "text"} onValueChange={(v) => { const u = [...fields]; u[fi].type = v; setFields(u); }}>
                          <SelectTrigger className="bg-[#14101c] border-white/10 text-white text-xs h-8 w-28"><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-[#14101c] border-white/10 text-white">
                            {["text", "textarea", "select", "checkbox", "date", "number", "email", "phone"].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <button onClick={() => { const u = [...fields]; u[fi].required = !u[fi].required; setFields(u); }}
                          className={`text-xs px-2 py-1 rounded ${field.required ? "bg-[#f7c935]/10 text-[#f7c935]" : "bg-white/5 text-slate-500"}`}>
                          {field.required ? "Requis" : "Optionnel"}
                        </button>
                        <button onClick={() => removeField(fi)} className="p-1 hover:bg-red-500/10 rounded"><Trash2 className="w-3.5 h-3.5 text-red-400" /></button>
                      </div>
                      <div className="flex gap-2">
                        <Input value={field.section || ""} onChange={(e) => { const u = [...fields]; u[fi].section = e.target.value; setFields(u); }} placeholder="Section (ex: Identite)" className="bg-[#14101c] border-white/10 text-white text-xs h-7 w-36" />
                        <Input value={field.placeholder || ""} onChange={(e) => { const u = [...fields]; u[fi].placeholder = e.target.value; setFields(u); }} placeholder="Aide / placeholder" className="bg-[#14101c] border-white/10 text-white text-xs h-7 flex-1" />
                        <span className="text-[9px] text-slate-600 whitespace-nowrap self-center">ID: {field.field_id || field.id || `f_${fi}`}</span>
                      </div>
                      {(field.type === "select" || field.type === "checkbox") && (
                        <Input value={(field.options || []).join(", ")} onChange={(e) => { const u = [...fields]; u[fi].options = e.target.value.split(",").map(s => s.trim()); setFields(u); }}
                          placeholder="Options (separees par virgule)" className="bg-[#14101c] border-white/10 text-white text-xs h-7 mt-2" />
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Conditions editor */}
              <div className="border-t border-white/10 pt-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-bold text-sm flex items-center gap-2" style={{ color: "#8b5cf6" }}><GitBranch className="w-4 h-4" /> Logique conditionnelle ({qstConditions.length})</h4>
                  <Button size="sm" onClick={addCondition} variant="outline" className="border-white/10 text-white hover:bg-white/5 text-xs"><Plus className="w-3 h-3 mr-1" /> Condition</Button>
                </div>
                <p className="text-[10px] text-slate-500 mb-2">Si un champ a une certaine valeur, alors afficher d'autres champs.</p>
                <div className="space-y-2 max-h-[200px] overflow-y-auto">
                  {qstConditions.map((cond, ci) => (
                    <div key={ci} className="bg-[#0b0811] border border-[#8b5cf6]/20 rounded-lg p-3" data-testid={`qst-condition-${ci}`}>
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <span className="text-[10px] text-[#8b5cf6] font-bold">SI</span>
                        <select value={cond.if_field_id || ""} onChange={(e) => { const u = [...qstConditions]; u[ci].if_field_id = e.target.value; setQstConditions(u); }}
                          className="bg-[#14101c] border border-white/10 text-white rounded px-2 py-1 text-xs h-7 flex-1 min-w-[120px]">
                          <option value="">-- Champ --</option>
                          {fields.map((f, fi) => <option key={fi} value={f.field_id || f.id || `f_${fi}`}>{f.label || `Champ ${fi + 1}`}</option>)}
                        </select>
                        <select value={cond.operator || "equals"} onChange={(e) => { const u = [...qstConditions]; u[ci].operator = e.target.value; setQstConditions(u); }}
                          className="bg-[#14101c] border border-white/10 text-white rounded px-2 py-1 text-xs h-7">
                          <option value="equals">= egal a</option>
                          <option value="not_equals">!= different de</option>
                          <option value="contains">contient</option>
                          <option value="not_empty">non vide</option>
                        </select>
                        {cond.operator !== "not_empty" && (
                          <Input value={cond.value || ""} onChange={(e) => { const u = [...qstConditions]; u[ci].value = e.target.value; setQstConditions(u); }}
                            placeholder="Valeur" className="bg-[#14101c] border-white/10 text-white text-xs h-7 w-28" />
                        )}
                        <button onClick={() => removeCondition(ci)} className="p-1 hover:bg-red-500/10 rounded"><Trash2 className="w-3.5 h-3.5 text-red-400" /></button>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[10px] text-green-400 font-bold"><ArrowRight className="w-3 h-3 inline" /> ALORS AFFICHER</span>
                        <div className="flex gap-1 flex-wrap flex-1">
                          {fields.map((f, fi) => {
                            const fid = f.field_id || f.id || `f_${fi}`;
                            const selected = (cond.then_show_fields || []).includes(fid);
                            return (
                              <button key={fi} onClick={() => {
                                const u = [...qstConditions];
                                if (selected) u[ci].then_show_fields = (u[ci].then_show_fields || []).filter(x => x !== fid);
                                else u[ci].then_show_fields = [...(u[ci].then_show_fields || []), fid];
                                setQstConditions(u);
                              }} className={`text-[9px] px-1.5 py-0.5 rounded-full border transition-colors ${selected ? "bg-green-500/20 text-green-400 border-green-500/30" : "bg-white/5 text-slate-500 border-white/5 hover:border-white/10"}`}>
                                {f.label || `Champ ${fi + 1}`}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>)}

            <Button onClick={handleSave} disabled={saving} className="w-full bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825] font-bold" data-testid="builder-save-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              {editingId ? "Modifier" : "Creer"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* AI Generate Dialog - Doc Templates */}
      <Dialog open={showAI} onOpenChange={setShowAI}>
        <DialogContent className="bg-[#14101c] border-white/10 text-white max-w-lg">
          <DialogHeader><DialogTitle className="text-xl font-bold flex items-center gap-2"><Brain className="w-5 h-5" style={{ color: "#f7c935" }} /> Generer un modele par IA</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm text-slate-400">Decrivez le document que vous souhaitez creer et l'IA generera les champs du formulaire.</p>
            <Textarea value={aiPrompt} onChange={(e) => setAiPrompt(e.target.value)} placeholder="Ex: Une convention de formation professionnelle avec les coordonnees du client, les details de la formation, les conditions financieres et les signatures..." rows={4} className={inputCls} data-testid="ai-doc-prompt" />
            <Button onClick={handleAIGenerate} disabled={generatingAI || !aiPrompt} className="w-full bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825] font-bold" data-testid="ai-doc-generate-btn">
              {generatingAI ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Generation en cours...</> : <><Brain className="w-4 h-4 mr-2" /> Generer</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* AI Generate Dialog - Acc Templates */}
      <Dialog open={showAccAI} onOpenChange={setShowAccAI}>
        <DialogContent className="bg-[#14101c] border-white/10 text-white max-w-lg">
          <DialogHeader><DialogTitle className="text-xl font-bold flex items-center gap-2"><Brain className="w-5 h-5" style={{ color: "#f7c935" }} /> Generer un parcours par IA</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm text-slate-400">Decrivez le parcours d'accompagnement souhaite et l'IA generera les phases et taches automatiquement.</p>
            <Textarea value={accAiPrompt} onChange={(e) => setAccAiPrompt(e.target.value)} placeholder="Ex: Un accompagnement de 6 mois pour la creation d'entreprise avec audit initial, business plan, demarches administratives, strategie commerciale et suivi post-lancement..." rows={4} className={inputCls} data-testid="ai-acc-prompt" />
            <Button onClick={handleAccAIGenerate} disabled={generatingAccAI || !accAiPrompt} className="w-full bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825] font-bold" data-testid="ai-acc-generate-btn">
              {generatingAccAI ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Generation en cours...</> : <><Brain className="w-4 h-4 mr-2" /> Generer le parcours</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* AI Generate Dialog - Services */}
      <Dialog open={showSvcAI} onOpenChange={setShowSvcAI}>
        <DialogContent className="bg-[#14101c] border-white/10 text-white max-w-lg">
          <DialogHeader><DialogTitle className="text-xl font-bold flex items-center gap-2"><Brain className="w-5 h-5" style={{ color: "#f7c935" }} /> Generer un service par IA</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm text-slate-400">Choisissez le type de document a generer et decrivez le service.</p>
            <div className="flex gap-2">
              <button onClick={() => setSvcAiKind("fiche_info")}
                className={`flex-1 p-3 rounded-lg border text-sm text-left transition-colors ${svcAiKind === "fiche_info" ? "border-[#f7c935] bg-[#f7c935]/10 text-[#f7c935]" : "border-white/10 text-slate-400 hover:border-white/20"}`}>
                <div className="font-bold mb-1">Fiche d'information</div>
                <div className="text-xs opacity-70">Formulaire de recueil des infos client</div>
              </button>
              <button onClick={() => setSvcAiKind("cahier_charges")}
                className={`flex-1 p-3 rounded-lg border text-sm text-left transition-colors ${svcAiKind === "cahier_charges" ? "border-[#f7c935] bg-[#f7c935]/10 text-[#f7c935]" : "border-white/10 text-slate-400 hover:border-white/20"}`}>
                <div className="font-bold mb-1">Cahier des charges</div>
                <div className="text-xs opacity-70">Contexte, objectifs, livrables, planning</div>
              </button>
            </div>
            <Textarea value={svcAiPrompt} onChange={(e) => setSvcAiPrompt(e.target.value)} placeholder={svcAiKind === "fiche_info" ? "Ex: Service de creation de site web e-commerce, j'ai besoin de recueillir les infos du client..." : "Ex: Cahier des charges pour une refonte du systeme d'information..."} rows={4} className={inputCls} data-testid="ai-svc-prompt" />
            <Button onClick={handleSvcAIGenerate} disabled={generatingSvcAI || !svcAiPrompt} className="w-full bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825] font-bold" data-testid="ai-svc-generate-btn">
              {generatingSvcAI ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Generation en cours...</> : <><Brain className="w-4 h-4 mr-2" /> Generer</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* AI Generate Dialog - Questionnaires */}
      <Dialog open={showQstAI} onOpenChange={setShowQstAI}>
        <DialogContent className="bg-[#14101c] border-white/10 text-white max-w-lg">
          <DialogHeader><DialogTitle className="text-xl font-bold flex items-center gap-2"><Brain className="w-5 h-5" style={{ color: "#8b5cf6" }} /> Generer un questionnaire par IA</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm text-slate-400">Decrivez votre questionnaire de qualification prospects et l'IA generera les champs et conditions automatiquement.</p>
            <Textarea value={qstAiPrompt} onChange={(e) => setQstAiPrompt(e.target.value)} placeholder="Ex: Questionnaire pour qualifier des prospects en formation automobile - je veux savoir leur statut pro, le module qui les interesse, leur budget, delai de demarrage, et si ils sont salaries montrer des questions specifiques..." rows={4} className={inputCls} data-testid="ai-qst-prompt" />
            <Button onClick={handleQstAIGenerate} disabled={generatingQstAI || !qstAiPrompt} className="w-full bg-[#8b5cf6] text-white hover:bg-[#7c3aed] font-bold" data-testid="ai-qst-generate-btn">
              {generatingQstAI ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Generation en cours...</> : <><Brain className="w-4 h-4 mr-2" /> Generer le questionnaire</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ═══ BALISES (Tags) MANAGEMENT COMPONENT ═══
const CAT_COLORS = { entreprise: "#f7c935", client: "#3b82f6", formateur: "#22c55e", stagiaire: "#8b5cf6", formation: "#f97316", document: "#ef4444", facturation: "#06b6d4" };

function BalisesTab({ tags, tagCategories, newTag, setNewTag, editingTag, setEditingTag, savingTag, handleCreateTag, handleUpdateTag, handleDeleteTag, search, setSearch }) {
  const tagsByCategory = {};
  tags.forEach(t => { if (!tagsByCategory[t.category]) tagsByCategory[t.category] = []; tagsByCategory[t.category].push(t); });

  const filteredTags = search ? tags.filter(t => t.label.toLowerCase().includes(search.toLowerCase()) || t.key.toLowerCase().includes(search.toLowerCase())) : null;

  return (
    <div data-testid="balises-tab">
      {/* Create new tag form */}
      <div className="bg-[#14101c] border border-white/10 rounded-xl p-5 mb-6">
        <div className="text-xs font-bold text-[#f7c935] uppercase tracking-wider mb-3 flex items-center gap-1.5">
          <Plus className="w-3.5 h-3.5" /> Creer une nouvelle balise
        </div>
        <div className="flex items-end gap-3">
          <div className="flex-1">
            <label className="text-[10px] text-slate-500 mb-1 block">Cle (identifiant)</label>
            <Input value={newTag.key} onChange={e => setNewTag({ ...newTag, key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_") })} placeholder="ex: numero_dossier" className="bg-[#0b0811] border-white/10 text-white text-sm" data-testid="new-tag-key" />
          </div>
          <div className="flex-1">
            <label className="text-[10px] text-slate-500 mb-1 block">Libelle</label>
            <Input value={newTag.label} onChange={e => setNewTag({ ...newTag, label: e.target.value })} placeholder="ex: Numero de dossier" className="bg-[#0b0811] border-white/10 text-white text-sm" data-testid="new-tag-label" />
          </div>
          <div className="w-44">
            <label className="text-[10px] text-slate-500 mb-1 block">Categorie</label>
            <select value={newTag.category} onChange={e => setNewTag({ ...newTag, category: e.target.value })} className="w-full bg-[#0b0811] border border-white/10 text-white rounded-lg px-3 py-2 text-sm" data-testid="new-tag-category">
              {(tagCategories || []).map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          </div>
          <Button onClick={handleCreateTag} disabled={savingTag || !newTag.key || !newTag.label} className="bg-[#f7c935] text-[#0b0811] hover:bg-[#e6b825] font-bold h-10" data-testid="create-tag-btn">
            {savingTag ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" /> Creer</>}
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
        <Input placeholder="Rechercher une balise..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 bg-[#0b0811] border-white/10 text-white placeholder:text-slate-500 focus:border-[#f7c935]" />
      </div>

      {/* Tags by category */}
      {filteredTags ? (
        <div className="bg-[#14101c] border border-white/10 rounded-xl p-5">
          <div className="text-xs font-bold text-slate-400 mb-3">Resultats ({filteredTags.length})</div>
          <div className="space-y-1.5">
            {filteredTags.map(t => <TagRow key={t.tag_id} tag={t} editingTag={editingTag} setEditingTag={setEditingTag} handleUpdateTag={handleUpdateTag} handleDeleteTag={handleDeleteTag} tagCategories={tagCategories} />)}
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {Object.entries(tagsByCategory).map(([cat, catTags]) => (
            <div key={cat} className="bg-[#14101c] border border-white/10 rounded-xl overflow-hidden">
              <div className="px-5 py-3 flex items-center gap-2 border-b border-white/5" style={{ background: (CAT_COLORS[cat] || "#64748b") + "08" }}>
                <div className="w-2.5 h-2.5 rounded-full" style={{ background: CAT_COLORS[cat] || "#64748b" }} />
                <span className="text-xs font-bold uppercase tracking-wider" style={{ color: CAT_COLORS[cat] || "#64748b" }}>{cat}</span>
                <span className="text-[10px] text-slate-600 ml-1">({catTags.length})</span>
              </div>
              <div className="p-3 space-y-1">
                {catTags.map(t => <TagRow key={t.tag_id} tag={t} editingTag={editingTag} setEditingTag={setEditingTag} handleUpdateTag={handleUpdateTag} handleDeleteTag={handleDeleteTag} tagCategories={tagCategories} />)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TagRow({ tag, editingTag, setEditingTag, handleUpdateTag, handleDeleteTag, tagCategories }) {
  const isEditing = editingTag?.tag_id === tag.tag_id;
  const [editData, setEditData] = useState({ label: tag.label, key: tag.key, category: tag.category });

  if (isEditing) {
    return (
      <div className="flex items-center gap-2 bg-[#0b0811] border border-[#f7c935]/30 rounded-lg p-2.5" data-testid={`tag-edit-${tag.tag_id}`}>
        <Input value={editData.label} onChange={e => setEditData({ ...editData, label: e.target.value })} className="bg-[#14101c] border-white/10 text-white text-xs h-7 flex-1" placeholder="Libelle" />
        {!tag.system && <Input value={editData.key} onChange={e => setEditData({ ...editData, key: e.target.value })} className="bg-[#14101c] border-white/10 text-white text-xs h-7 w-36 font-mono" placeholder="Cle" />}
        <select value={editData.category} onChange={e => setEditData({ ...editData, category: e.target.value })} className="bg-[#14101c] border border-white/10 text-white text-[10px] rounded px-1.5 py-1 h-7 w-28">
          {(tagCategories || []).map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
        </select>
        <button onClick={() => { handleUpdateTag(tag.tag_id, editData); }} className="text-[10px] px-2 py-1 rounded bg-[#f7c935]/20 text-[#f7c935] font-bold hover:bg-[#f7c935]/30">OK</button>
        <button onClick={() => setEditingTag(null)} className="text-[10px] px-2 py-1 rounded bg-white/5 text-slate-400 hover:bg-white/10">Annuler</button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 hover:bg-white/3 rounded-lg px-2.5 py-1.5 group" data-testid={`tag-row-${tag.tag_id}`}>
      <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-white/5 text-slate-400">{`{${tag.key}}`}</span>
      <span className="text-sm text-white flex-1">{tag.label}</span>
      {tag.system && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-blue-500/10 text-blue-400 border border-blue-500/20">Systeme</span>}
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <button onClick={() => { setEditingTag(tag); setEditData({ label: tag.label, key: tag.key, category: tag.category }); }} className="p-1 rounded hover:bg-white/10" data-testid={`tag-edit-btn-${tag.tag_id}`}>
          <Pencil className="w-3 h-3 text-slate-400" />
        </button>
        {!tag.system && (
          <button onClick={() => handleDeleteTag(tag.tag_id)} className="p-1 rounded hover:bg-red-500/10" data-testid={`tag-delete-btn-${tag.tag_id}`}>
            <Trash2 className="w-3 h-3 text-red-400" />
          </button>
        )}
      </div>
    </div>
  );
}


function RoutinePdfConfigTab({ api }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    api.get("/ai-agents/crm/routines/pdf-config").then(r => {
      const defaults = { show_header: true, show_metadata: true, show_score: true, show_priority: true, show_reason: true, show_action: true, show_footer: true, company_name: "Blitzz", accent_color: "#f7c935" };
      setConfig({ ...defaults, ...r.data });
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/ai-agents/crm/routines/pdf-config", config);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch {}
    setSaving(false);
  };

  if (loading || !config) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#f7c935]" /></div>;

  const toggles = [
    { key: "show_header", label: "Titre de la routine", desc: "Affiche le nom et le type de routine en haut du PDF" },
    { key: "show_metadata", label: "Metadonnees", desc: "Date d'execution, operateur, nombre d'elements analyses" },
    { key: "show_score", label: "Score de qualification", desc: "Affiche le score /10 de chaque prospect" },
    { key: "show_priority", label: "Niveau de priorite", desc: "Haute, Moyenne ou Basse pour chaque prospect" },
    { key: "show_reason", label: "Raison", desc: "Justification du score et de la priorite" },
    { key: "show_action", label: "Action recommandee", desc: "Suggestion d'action pour chaque prospect" },
    { key: "show_footer", label: "Pied de page", desc: "Nom de l'entreprise et date en bas du document" },
  ];

  return (
    <div className="space-y-5" data-testid="routine-pdf-config">
      <div className="bg-[#14101c] border border-white/10 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#f7c935]/10">
            <FileText className="w-5 h-5 text-[#f7c935]" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-white">Configuration PDF Routines IA</h3>
            <p className="text-[11px] text-slate-500">Choisissez les informations a inclure dans l'export PDF des routines</p>
          </div>
        </div>

        {/* Toggle switches */}
        <div className="space-y-3">
          {toggles.map(t => (
            <div key={t.key} className="flex items-center justify-between p-3 bg-[#0b0811] border border-white/[0.06] rounded-lg" data-testid={`pdf-toggle-${t.key}`}>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-white">{t.label}</div>
                <div className="text-[10px] text-slate-500">{t.desc}</div>
              </div>
              <button onClick={() => setConfig({ ...config, [t.key]: !config[t.key] })}
                className={`relative w-11 h-6 rounded-full transition-colors ${config[t.key] ? "bg-[#f7c935]" : "bg-slate-700"}`}>
                <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${config[t.key] ? "translate-x-[22px]" : "translate-x-0.5"}`} />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Company name + accent color */}
      <div className="bg-[#14101c] border border-white/10 rounded-xl p-6">
        <h4 className="text-sm font-bold text-white mb-4">Personnalisation</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-[10px] text-slate-500 uppercase font-bold mb-1.5 block">Nom de l'entreprise (pied de page)</label>
            <input value={config.company_name || ""} onChange={e => setConfig({ ...config, company_name: e.target.value })}
              className="w-full bg-[#0b0811] border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
              data-testid="pdf-company-name" />
          </div>
          <div>
            <label className="text-[10px] text-slate-500 uppercase font-bold mb-1.5 block">Couleur d'accent</label>
            <div className="flex items-center gap-2">
              <input type="color" value={config.accent_color || "#f7c935"} onChange={e => setConfig({ ...config, accent_color: e.target.value })}
                className="w-10 h-10 rounded-lg cursor-pointer border-0 bg-transparent" data-testid="pdf-accent-color" />
              <input value={config.accent_color || ""} onChange={e => setConfig({ ...config, accent_color: e.target.value })}
                className="flex-1 bg-[#0b0811] border border-white/10 rounded-lg px-3 py-2 text-sm text-white font-mono" />
            </div>
          </div>
        </div>
      </div>

      {/* Preview hint + Save */}
      <div className="flex items-center justify-between">
        <p className="text-[10px] text-slate-500">Les modifications s'appliquent a tous les futurs exports PDF de routines IA.</p>
        <Button onClick={save} disabled={saving} className="bg-[#f7c935] text-[#0b0811] hover:bg-[#e6b825] font-bold" data-testid="save-pdf-config">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : saved ? <CheckCircle className="w-4 h-4 mr-1" /> : <Save className="w-4 h-4 mr-1" />}
          {saved ? "Sauvegarde !" : "Sauvegarder"}
        </Button>
      </div>
    </div>
  );
}
